import { useState, useEffect, useCallback, useRef } from 'react';
import {
    SIDE_PANEL_RESIZE_HANDLE_CLASS_NAME,
    SIDE_PANEL_RESIZE_HANDLE_DRAGGING_CLASS_NAME
} from '../HBSidePanel.consts';

interface Props {
    initialWidth: number;
    isResizable: boolean;
    minWidth?: number;
}

export const useResizableWidth = ({
    initialWidth,
    isResizable,
    minWidth
}: Props) => {
    const [currentWidth, setCurrentWidth] = useState(initialWidth);
    const startClientXRef = useRef<number | null>(null);

    const handleMouseMove = useCallback(
        (event: MouseEvent) => {
            if (startClientXRef.current === null) {
                return;
            }
            const desiredWidth = window.innerWidth - event.clientX;
            const min = minWidth ?? 0;
            setCurrentWidth(desiredWidth < min ? min : desiredWidth);
        },
        [minWidth]
    );

    useEffect(() => {
        if (!isResizable) {
            return;
        }

        const handleMouseUp = () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = '';
            document
                .querySelector(`.${SIDE_PANEL_RESIZE_HANDLE_CLASS_NAME}`)
                ?.classList.remove(
                    SIDE_PANEL_RESIZE_HANDLE_DRAGGING_CLASS_NAME
                );
            startClientXRef.current = null;
        };

        const handleMouseDown = (event: MouseEvent) => {
            event.preventDefault();
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = 'none';
            (event.currentTarget as HTMLElement).classList.add(
                SIDE_PANEL_RESIZE_HANDLE_DRAGGING_CLASS_NAME
            );
            startClientXRef.current = event.clientX;
        };

        const resizeHandle = document.querySelector(
            `.${SIDE_PANEL_RESIZE_HANDLE_CLASS_NAME}`
        );
        resizeHandle?.addEventListener('mousedown', handleMouseDown);

        return () => {
            resizeHandle?.removeEventListener('mousedown', handleMouseDown);
            document.body.style.userSelect = '';
        };
    }, [isResizable, handleMouseMove]);

    return [currentWidth];
};
